import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface SegOption<V extends string = string> {
  value: V;
  label: ReactNode;
}

export interface SegProps<V extends string = string> {
  options: readonly SegOption<V>[];
  value: V;
  onChange: (value: V) => void;
  /** Optional accessible name for the radiogroup. */
  'aria-label'?: string;
}

// Segmented control (role=radiogroup/radio): single-select enclosed button
// group with a raised active segment, per the design kit. The value-select,
// enclosed-rail counterpart to the underline Tabs strip. Controlled: caller
// owns the active value. Labels flow in as props (no copy lives here).
export function Seg<V extends string = string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
}: SegProps<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex((o) => o.value === value);

  // WAI-ARIA radio group: arrow/Home/End move focus AND check the new radio,
  // wrapping at the ends.
  function focusAndSelect(index: number) {
    const next = (index + options.length) % options.length;
    const opt = options[next];
    if (!opt) return;
    onChange(opt.value);
    refs.current[next]?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusAndSelect(index + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusAndSelect(index - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusAndSelect(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusAndSelect(options.length - 1);
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        gap: 'var(--s-1)',
        padding: 'var(--s-1)',
        background: 'var(--bg-2)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
      }}
    >
      {options.map((o, i) => {
        const active = o.value === value;
        // Roving tabindex: only the checked radio is tabbable (first if none).
        const tabbable = selectedIndex === -1 ? i === 0 : active;
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={tabbable ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            style={{
              padding: 'var(--s-2) var(--s-3)',
              border: 'none',
              borderRadius: 'var(--r-1)',
              background: active ? 'var(--bg-1)' : 'transparent',
              color: active ? 'var(--fg-1)' : 'var(--fg-2)',
              boxShadow: active ? 'var(--shadow-1)' : 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--fz-13)',
              fontWeight: 500,
              transition:
                'background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out)',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
